import { GridCellParams } from '@mui/x-data-grid-premium'
import { GridColDef } from '@mui/x-data-grid-pro'
import { breakpointSizes, getBreakpointDown } from '@paywithextend/components'
import { AxiosError } from 'axios'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import useFilters from 'ext-ui/hooks/filters/filters'
import { spacing } from 'ext-ui/style/spacing'
import React, { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { BackendErrorToastMessage } from 'src/app/Toast/Toast'
import Box from 'src/components/Box/Box'
import CheckboxWithTooltip from 'src/components/CheckboxWithTooltip/CheckboxWithTooltip'
import { chevronColumnDef } from 'src/components/DataGrid/cells/ChevronCell'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import DataGridEditableCell from 'src/components/DataGrid/cells/DataGridEditableCell/DataGridEditableCell'
import TextFieldCell from 'src/components/DataGrid/cells/TextFieldCell/TextFieldCell'
import DataGrid from 'src/components/DataGrid/DataGrid'
import ExpenseCategoriesListFilter from 'src/components/ExpenseCategoriesList/ExpenseCategoriesListFilter'
import ExpenseCategoriesListEmptyState from 'src/components/ExpenseCategoriesListEmptyState/ExpenseCategoriesListEmptyState'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import {
  FormFieldsValidation,
  useBackgroundDispatch,
  useFormFields,
} from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { connectedPush } from 'src/navigation/Lnk'
import { expenseCategoryLabelsUrl } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  ExpenseCategoryFiltersRequestParams,
  ExpenseCategoryResponse,
  ExpenseCategorySortField,
  ExpenseCategoryType,
  PatchExpenseCategoryRequest,
  usePatchExpenseCategory,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import { getOrganizationIntegrations } from 'src/redux/actions/data/organizations.actions'
import { selectOrganizationIdFromRoute } from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectBrandColorHighlight } from 'src/redux/selectors/session.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'
import { parseBooleanQueryParamFromArray } from 'src/utils/string.utils'
import {
  stringIsEmpty,
  stringLengthIsLessThan,
} from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

interface ExpenseCategoriesListRow extends ExpenseCategoryResponse {
  index: number
}

enum ExpenseCategoryStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

enum RequiredStatus {
  Required = 'Required',
  NotRequired = 'Not Required',
}

interface ExpenseCategoryFilters {
  search?: string
  status?: string[]
  requiredForTransactions?: string[]
  requiredForVirtualCards?: string[]
}

interface ExpenseCategoriesListProps {
  initialFilterValues: Partial<ExpenseCategoryFilters>
}

const ExpenseCategoriesList: React.FC<ExpenseCategoriesListProps> = (
  props,
): React.ReactElement => {
  const dispatch = useBackgroundDispatch()
  const translate = useTranslate()

  const {
    filters: currFilters,
    apiQueryParams,
    handleFilterChange,
    isDirty,
  } = useFilters<ExpenseCategoryFilters, ExpenseCategoryFiltersRequestParams>({
    defaultFilters: {
      status: [],
      requiredForTransactions: [],
      requiredForVirtualCards: [],
      search: '',
    },
    apiQueryParams: (filters: Partial<ExpenseCategoryFilters>) => {
      const { status: _status, ...restFilters } = filters
      const cleanedFilters = Object.fromEntries(
        Object.entries(restFilters).filter(([_, value]) => value !== ''),
      )
      return {
        ...cleanedFilters,
        active: parseBooleanQueryParamFromArray(
          filters.status ?? [],
          ExpenseCategoryStatus.Active,
        ),
        requiredForTransactions: parseBooleanQueryParamFromArray(
          filters.requiredForTransactions ?? [],
          RequiredStatus.Required,
        ),
        requiredForVirtualCards: parseBooleanQueryParamFromArray(
          filters.requiredForVirtualCards ?? [],
          RequiredStatus.Required,
        ),
        sortField: ExpenseCategorySortField.Name,
        sortDirection: 'ASC',
      }
    },
  })

  const orgId = useSelector(selectOrganizationIdFromRoute)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const brandColorHighlight = useSelector(selectBrandColorHighlight)

  const filterSectionRef: React.MutableRefObject<HTMLDivElement | null> =
    useRef(null)

  const { mutateAsync: patchExpCat, isPending: patchExpCatLoading } =
    usePatchExpenseCategory()

  const { EDIT_EXPENSE_CATEGORIES: hasEditExpCatPermissions } =
    useQueryUserFeatures([UserFeature.EDIT_EXPENSE_CATEGORIES]).data || {}
  const { VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures } =
    useQueryUserFeatures([UserFeature.VIEW_VIRTUAL_CARD_FEATURES]).data || {}

  const patchExpCatSucceeded = async ({
    id,
    body,
  }: {
    id: string
    body: PatchExpenseCategoryRequest
  }): Promise<boolean> => {
    return await patchExpCat({ id, body })
      .then(() => true)
      .catch((e: AxiosError<BackendErrorPayload<string>>) => {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: <BackendErrorToastMessage error={e} />,
            failureColorScheme: true,
            hideImage: true,
          }),
        )
        return false
      })
  }

  useEffect((): void => {
    if (orgId) {
      dispatch(getOrganizationIntegrations(orgId))
    }
  }, [])

  const {
    data: expenseDetails,
    isFetching,
    isFetched,
    isSuccess,
  } = useQueryExpenseCategories({
    filters: apiQueryParams,
  })

  const {
    setFields: setExpCat,
    fieldsErrors: expCatErrors,
    setFieldsErrors,
    hasFieldsErrors,
    setErrorsAndReturnDidPassValidation,
  } = useFormFields<PatchExpenseCategoryRequest>(
    {
      name: '',
    },
    (): FormFieldsValidation<PatchExpenseCategoryRequest> => ({
      name: [stringIsEmpty(), stringLengthIsLessThan(255)],
    }),
  )

  const rows: ExpenseCategoriesListRow[] =
    expenseDetails !== undefined
      ? expenseDetails.map((expCat, i) => {
          return {
            index: i,
            ...expCat,
          }
        })
      : []

  const columns = useMemo(() => {
    const cols: Array<GridColDef<ExpenseCategoriesListRow>> = [
      {
        field: 'name',
        headerName: translate('Category Name'),
        disableColumnMenu: true,
        sortable: false,
        hideable: false,
        editable: hasEditExpCatPermissions,
        flex: 1,
        minWidth: 200,
        renderCell: (params: any): React.ReactElement => {
          return (
            <DataGridEditableCell
              tooltipContent={
                params.row.editable && hasEditExpCatPermissions
                  ? translate('Edit category name')
                  : translate('Cannot edit name')
              }
              params={params}
              editMode={'row'}
              value={params.row.name}
            />
          )
        },
        renderEditCell: (params: any): React.ReactElement => {
          return (
            <TextFieldCell
              characterLimit={255}
              tooltip={translate('Edit category name')}
              placeholder={translate('Enter category name')}
              microcopy={translate('Press Enter to save or Esc to cancel')}
              params={params}
              editMode={'row'}
              errorText={expCatErrors.name?.[0]}
              clearError={(): void => setFieldsErrors({})}
              setValue={(val: string): void => setExpCat({ name: val })}
              disabled={patchExpCatLoading}
            />
          )
        },
      },
      {
        field: 'code',
        headerName: translate('Code'),
        disableColumnMenu: true,
        sortable: false,
        hideable: false,
        editable: false,
        flex: 1,
        minWidth: 150,
        width: 200,
        renderCell: (params: any): React.ReactElement => {
          return <DataGridCell params={params} text={params.row.code} />
        },
      },
      {
        field: 'active',
        headerName: translate('Active'),
        description: translate(
          'When active, everyone in the org can code transactions to the category. When inactive, the category will be hidden from view.',
        ),
        disableColumnMenu: true,
        sortable: false,
        hideable: false,
        editable: false,
        minWidth: 128,
        renderCell: (params: any): React.ReactElement => {
          return (
            <DataGridCellWrapper params={params}>
              <OnOffSwitch
                testId={'isActiveSwitch'}
                onBgColor={brandColorHighlight}
                smallMode
                defaultOnOffState={params.row.active}
                onChange={(): Promise<boolean> =>
                  patchExpCatSucceeded({
                    id: params.row.id,
                    body: { active: !params.row.active },
                  })
                }
                disabled={
                  (activeIntegration !== undefined &&
                    params.row.type === ExpenseCategoryType.Account) ||
                  !hasEditExpCatPermissions
                }
                tooltipProps={{
                  placement: 'bottom-start',
                  tooltipContent:
                    activeIntegration !== undefined &&
                    params.row.type === ExpenseCategoryType.Account
                      ? translate(
                          'This setting is locked because your organization is connected to accounting software',
                        )
                      : undefined,
                }}
              />
            </DataGridCellWrapper>
          )
        },
      },
      hasVirtualCardFeatures && {
        field: 'requiredForVirtualCards',
        headerName: translate('Required for Virtual Cards'),
        description: translate(
          'If required, this category must be coded whenever a virtual card is created or updated.',
        ),
        disableColumnMenu: true,
        sortable: false,
        hideable: false,
        editable: false,
        minWidth: 220,
        renderCell: (params: any): React.ReactElement => {
          return (
            <DataGridCellWrapper params={params}>
              <Checkbox
                data-testid={'isRequiredCheckbox'}
                aria-label={translate('Required for Virtual Cards Checkbox')}
                checked={params.row.requiredForVirtualCards}
                disabled={!params.row.active || !hasEditExpCatPermissions}
                onClick={(e): void => {
                  e.stopPropagation()
                  patchExpCatSucceeded({
                    id: params.row.id,
                    body: {
                      requiredForVirtualCards:
                        !params.row.requiredForVirtualCards,
                    },
                  })
                }}
              />
            </DataGridCellWrapper>
          )
        },
      },
      {
        field: 'requiredForTransactions',
        headerName: translate('Required for Transactions'),
        description: translate(
          'If required, this category must be coded on transactions.',
        ),
        disableColumnMenu: true,
        sortable: false,
        hideable: false,
        editable: false,
        minWidth: 220,
        renderCell: (params: any): React.ReactElement => {
          return (
            <DataGridCellWrapper params={params}>
              <CheckboxWithTooltip
                data-testid={'isRequiredCheckbox'}
                aria-label={translate('Required for Transactions Checkbox')}
                checked={params.row.requiredForTransactions}
                disabled={
                  !params.row.active ||
                  (activeIntegration !== undefined &&
                    params.row.type === ExpenseCategoryType.Account) ||
                  !hasEditExpCatPermissions
                }
                onClick={(e): void => {
                  e.stopPropagation()
                  patchExpCatSucceeded({
                    id: params.row.id,
                    body: {
                      requiredForTransactions:
                        !params.row.requiredForTransactions,
                    },
                  })
                }}
                tooltipProps={{
                  placement: 'bottom-start',
                  tooltipContent:
                    activeIntegration !== undefined &&
                    params.row.type === ExpenseCategoryType.Account
                      ? translate(
                          'This setting is locked because your organization is connected to accounting software',
                        )
                      : undefined,
                }}
              />
            </DataGridCellWrapper>
          )
        },
      },
      chevronColumnDef,
    ].filter(Boolean) as Array<GridColDef<ExpenseCategoriesListRow>>
    // ^ Casting here is OK. We have conditional column rendering here so things
    // can get a little strange with typescript. .filter(Boolean) will remove any
    // false-y values from this array.

    if (activeIntegration !== undefined) {
      return cols.filter((col) => col.field !== 'code')
    } else {
      return cols
    }
  }, [
    activeIntegration,
    expCatErrors,
    patchExpCatLoading,
    hasEditExpCatPermissions,
  ])

  const showEmptyState = expenseDetails?.length === 0 && isFetched && !isDirty

  if (showEmptyState) {
    return <ExpenseCategoriesListEmptyState />
  }

  return (
    <Box
      css={css`
        display: flex;
        flex-direction: column;
        overflow: hidden;
      `}
      noPadding
      noShadow
      outlineBorder
    >
      <div
        css={css`
          display: flex;
          flex-direction: row;
          align-items: center;
          ${getBreakpointDown(breakpointSizes.SM)(css`
            flex-direction: column;
          `)}
        `}
      >
        <ExpenseCategoriesListFilter
          filterValues={currFilters}
          hasActiveIntegration={activeIntegration !== undefined}
          handleFilterChange={handleFilterChange}
          filterSectionRef={filterSectionRef}
        />
      </div>
      {!isFetching &&
      (expenseDetails === undefined || expenseDetails?.length === 0) ? (
        <TableEmptyState design={TableEmptyStateDesigns.ExpenseCategories} />
      ) : (
        <DataGrid
          rows={rows}
          columns={columns}
          filtersBody={currFilters}
          disableColumnFilter
          disableColumnReorder
          onRowClick={(row): void => {
            dispatch(
              connectedPush({
                pathname: expenseCategoryLabelsUrl(orgId, row.id.toString()),
                search: '',
                hash: '',
                state: {},
              }),
            )
          }}
          infiniteScrollProps={{
            // no actual infinite scroll, but want loading behaviour
            fetch: (): void => {
              return
            },
            loading: isFetching,
            complete: isSuccess,
          }}
          isCellEditable={(params: GridCellParams): boolean => {
            return params.row.editable
          }}
          onRowEditStop={(): void => {
            setFieldsErrors({})
          }}
          processRowUpdate={async (
            updatedRow,
          ): Promise<ExpenseCategoriesListRow> => {
            // check here for errors
            if (hasFieldsErrors || !setErrorsAndReturnDidPassValidation())
              return Promise.reject('FRONT_END_VALIDATION')

            return {
              ...(await patchExpCat({
                id: updatedRow.id,
                body: { name: updatedRow.name },
              })),
              index: updatedRow.index,
            }
          }}
          onProcessRowUpdateError={(
            e: string | AxiosError<BackendErrorPayload<string>>,
          ): void => {
            // handle case where setErrorsAndReturnDidPassValidation already handled setting front-end validation errors
            if (typeof e === 'string') return
            setFieldsErrors({ name: [e.response?.data.error ?? e.message] })
          }}
          sx={{
            '.MuiDataGrid-cell': {
              padding: `${spacing(2)} 0`,
            },
            '.MuiDataGrid-cell.MuiDataGrid-cell--editing': {
              padding: `${spacing(2)} 0`,
            },
          }}
          slotProps={{
            loadingOverlay: {
              // workaround for having non-0 height when loading on a non-fixed height datagrid
              noRowsVariant: 'skeleton',
            },
          }}
        />
      )}
    </Box>
  )
}

export default ExpenseCategoriesList
